import React, { useCallback, useEffect, useMemo, useState } from "react";
import SchoolDatePicker from "../../components/SchoolDatePicker/SchoolDatePicker";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import RefreshIcon from "@material-ui/icons/Refresh";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import SchoolUsageService from "./Locality/Service/schoolUsageService";

const useStyles = makeStyles((theme) => ({
  pageHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  pageTitle: {
    fontWeight: 700,
    fontSize: "1.9rem",
    color: "#0f2d4d",
    lineHeight: 1.2,
  },
  pageSubtitle: {
    color: "#6b7280",
    fontSize: "0.9rem",
    marginTop: 2,
  },
  filtersCard: {
    borderRadius: 14,
    border: "1px solid #e6eaf0",
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    background: "#fff",
  },
  tableCard: {
    borderRadius: 14,
    border: "1px solid #e6eaf0",
    background: "#fff",
  },
  headerCell: {
    backgroundColor: "#f4f6fa",
    color: "#46566b",
    fontWeight: 700,
    fontSize: "0.8rem",
  },
  primaryBtn: {
    backgroundColor: "#1769d6",
    color: "#fff",
    textTransform: "none",
    borderRadius: 8,
    "&:hover": { backgroundColor: "#125bbd" },
  },
  outlinedBtn: {
    textTransform: "none",
    borderRadius: 8,
    borderColor: "#0f2d4d",
    color: "#0f2d4d",
  },
  dayRow: {
    backgroundColor: "#fafbfd",
  },
  muted: { color: "#9aa6b6" },
  statChip: {
    display: "inline-block",
    minWidth: 28,
    textAlign: "center",
    fontWeight: 700,
    color: "#0f2d4d",
  },
}));

const toISODate = (d) => {
  try {
    return new Date(d).toISOString().slice(0, 10);
  } catch {
    return "";
  }
};

const defaultRange = () => {
  const to = new Date();
  const from = new Date(to);
  from.setDate(from.getDate() - 29);
  return { from: toISODate(from), to: toISODate(to) };
};

export default function SchoolUsage() {
  const classes = useStyles();
  const initial = useMemo(() => defaultRange(), []);
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    SchoolUsageService.getSummary({ from, to })
      .then((res) => setSummary(res || null))
      .catch((err) => {
        setSummary(null);
        setError(err?.response?.data?.message || err?.message || "Failed to load school usage");
      })
      .finally(() => setLoading(false));
  }, [from, to]);

  useEffect(() => {
    load();
  }, [load]);

  const schools = useMemo(() => {
    const list = Array.isArray(summary?.schools) ? summary.schools : [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (s) =>
        String(s.schooleName || "").toLowerCase().includes(q) ||
        String(s.city || "").toLowerCase().includes(q),
    );
  }, [summary, search]);

  const toggleExpand = (id) => {
    const key = String(id);
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const totals = summary?.grandTotals || {
    assignments: 0,
    calendarEvents: 0,
    notifications: 0,
    activities: 0,
  };

  return (
    <Box>
      <Box className={classes.pageHeader}>
        <Box>
          <Typography className={classes.pageTitle}>School Usage</Typography>
          <Typography className={classes.pageSubtitle}>
            Assignments, calendar events, notifications, and activities per school (day-wise).
          </Typography>
        </Box>
        <Button
          className={classes.primaryBtn}
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={load}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      <Card className={classes.filtersCard} elevation={0}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <SchoolDatePicker
              label="From"
              value={from}
              onChange={(iso) => setFrom(iso)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SchoolDatePicker
              label="To"
              value={to}
              onChange={(iso) => setTo(iso)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder="Search school or city"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "#9aa6b6" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button
              fullWidth
              className={classes.outlinedBtn}
              variant="outlined"
              onClick={load}
              disabled={loading}
            >
              Apply
            </Button>
          </Grid>
        </Grid>
        <Box mt={2} display="flex" flexWrap="wrap" style={{ gap: 16 }}>
          <Typography variant="body2" className={classes.muted}>
            Schools: <b style={{ color: "#0f2d4d" }}>{summary?.schoolCount ?? 0}</b>
          </Typography>
          <Typography variant="body2" className={classes.muted}>
            Assignments: <b style={{ color: "#0f2d4d" }}>{totals.assignments}</b>
          </Typography>
          <Typography variant="body2" className={classes.muted}>
            Calendar: <b style={{ color: "#0f2d4d" }}>{totals.calendarEvents}</b>
          </Typography>
          <Typography variant="body2" className={classes.muted}>
            Notifications: <b style={{ color: "#0f2d4d" }}>{totals.notifications}</b>
          </Typography>
          <Typography variant="body2" className={classes.muted}>
            Activities: <b style={{ color: "#0f2d4d" }}>{totals.activities}</b>
          </Typography>
        </Box>
      </Card>

      <Card className={classes.tableCard} elevation={0}>
        {loading ? (
          <Box p={4} display="flex" justifyContent="center">
            <CircularProgress size={32} />
          </Box>
        ) : error ? (
          <Box p={3}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.headerCell} style={{ width: 48 }} />
                  <TableCell className={classes.headerCell}>School</TableCell>
                  <TableCell className={classes.headerCell}>City</TableCell>
                  <TableCell className={classes.headerCell} align="right">
                    Assignments
                  </TableCell>
                  <TableCell className={classes.headerCell} align="right">
                    Calendar Events
                  </TableCell>
                  <TableCell className={classes.headerCell} align="right">
                    Notifications
                  </TableCell>
                  <TableCell className={classes.headerCell} align="right">
                    Activities
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {schools.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography className={classes.muted} style={{ padding: 24 }}>
                        No schools found for this range.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  schools.map((school) => {
                    const id = String(school._id);
                    const open = Boolean(expanded[id]);
                    const days = Array.isArray(school.dayWise) ? school.dayWise : [];
                    return (
                      <React.Fragment key={id}>
                        <TableRow hover>
                          <TableCell>
                            <IconButton size="small" onClick={() => toggleExpand(id)}>
                              {open ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Typography style={{ fontWeight: 600, color: "#0f2d4d" }}>
                              {school.schooleName || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>{school.city || "—"}</TableCell>
                          <TableCell align="right">
                            <span className={classes.statChip}>{school.totals?.assignments || 0}</span>
                          </TableCell>
                          <TableCell align="right">
                            <span className={classes.statChip}>{school.totals?.calendarEvents || 0}</span>
                          </TableCell>
                          <TableCell align="right">
                            <span className={classes.statChip}>{school.totals?.notifications || 0}</span>
                          </TableCell>
                          <TableCell align="right">
                            <span className={classes.statChip}>{school.totals?.activities || 0}</span>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={7} style={{ padding: 0, border: 0 }}>
                            <Collapse in={open} timeout="auto" unmountOnExit>
                              <Box p={2}>
                                {days.length === 0 ? (
                                  <Typography variant="body2" className={classes.muted}>
                                    No day-wise activity in this range.
                                  </Typography>
                                ) : (
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell className={classes.headerCell}>Date</TableCell>
                                        <TableCell className={classes.headerCell} align="right">
                                          Assignments
                                        </TableCell>
                                        <TableCell className={classes.headerCell} align="right">
                                          Calendar
                                        </TableCell>
                                        <TableCell className={classes.headerCell} align="right">
                                          Notifications
                                        </TableCell>
                                        <TableCell className={classes.headerCell} align="right">
                                          Activities
                                        </TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {days.map((day) => (
                                        <TableRow key={`${id}-${day.date}`} className={classes.dayRow}>
                                          <TableCell>{day.date}</TableCell>
                                          <TableCell align="right">{day.assignments || 0}</TableCell>
                                          <TableCell align="right">{day.calendarEvents || 0}</TableCell>
                                          <TableCell align="right">{day.notifications || 0}</TableCell>
                                          <TableCell align="right">{day.activities || 0}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>
    </Box>
  );
}
